/**
 * Walking routes: road centre lines in walking order, measured along their length (the prototype's routeList,
 * nearestOnRoutes and pointAt). Used to bin frontage along the route and to space shops along it.
 */
import type { Pt } from '../../shared/types';
import { clamp, cumLengths } from '../geom';

/** One road centre line with its distance along the whole route. */
export interface Route {
  poly: Pt[];
  /** Cumulative length at every sample. */
  cum: number[];
  len: number;
  /** Distance along the whole route where this road starts. */
  off: number;
  /** Widest width of the road (m). */
  w: number;
}

/** Builds routes from centre lines in the given walking order. */
export function makeRoutes(lines: { poly: Pt[]; w: number }[]): Route[] {
  let off = 0;
  return lines.filter(l => l.poly.length >= 2).map(l => {
    const cum = cumLengths(l.poly), len = cum[cum.length - 1] as number, rt = { poly: l.poly, cum, len, off, w: l.w };
    off += len;
    return rt;
  });
}

/** The nearest spot on any route: route index, distance along it and which side (+1 / -1) the point is on. */
export interface RouteHit { d: number; ri: number; s: number; side: number }

/** Nearest spot on the routes to (x, y); ri is -1 when there are no routes. */
export function nearestOnRoutes(routes: readonly Route[], x: number, y: number): RouteHit {
  let best: RouteHit = { d: 1e9, ri: -1, s: 0, side: 1 };
  routes.forEach((rt, ri) => {
    const P = rt.poly;
    for (let i = 0; i < P.length - 1; i++) {
      const a = P[i] as Pt, b = P[i + 1] as Pt, dx = b[0] - a[0], dy = b[1] - a[1], l2 = dx * dx + dy * dy;
      if (!l2) continue;
      const t = clamp(((x - a[0]) * dx + (y - a[1]) * dy) / l2, 0, 1), px = a[0] + t * dx, py = a[1] + t * dy, d = Math.hypot(x - px, y - py);
      if (d < best.d) best = { d, ri, s: (rt.cum[i] as number) + t * Math.sqrt(l2), side: Math.sign(dx * (y - a[1]) - dy * (x - a[0])) || 1 };
    }
  });
  return best;
}

/** The point at distance s along a route, with the unit normal there. */
export function pointAtRoute(rt: Route, s: number): { x: number; y: number; nx: number; ny: number } {
  const P = rt.poly;
  s = clamp(s, 0, rt.len);
  for (let i = 0; i < P.length - 1; i++) {
    const c0 = rt.cum[i] as number, c1 = rt.cum[i + 1] as number;
    if (c1 >= s) {
      const a = P[i] as Pt, b = P[i + 1] as Pt, l = c1 - c0 || 1, t = (s - c0) / l, dx = (b[0] - a[0]) / l, dy = (b[1] - a[1]) / l;
      return { x: a[0] + (b[0] - a[0]) * t, y: a[1] + (b[1] - a[1]) * t, nx: -dy, ny: dx };
    }
  }
  const q = P[P.length - 1] as Pt;
  return { x: q[0], y: q[1], nx: 0, ny: 1 };
}
